<template>
  <div
    class='checklist-wrapper'
    ref="checklistRef"
  >
    <div
      v-for='(el, i) in list' :key='`el-${el.keyId}`'
      class="element"
      :data-checklist-id="el.keyId"
    >
      <Checkbox
        :modelValue='el.state'
        @update:modelValue="check(i, $event)"
        :slotIsClickable="false"
      >
        <EditorInput
          class="input"
          :title="$t('task.Type_item')"
          :value="getEditorValue(el)"
          :render="getRenderValue(el)"
          :mentionButton="false"
          :attachmentButton="false"
          :recordingButton="false"
          emojiPickerBelow
          :isHideFormat="true"
          :submitOnFocusOut="true"
          @submit='update($event, i)'
          @update:opened="setEditingIndex($event, i)"
        />

        <template #actions>
          <div
            v-if="editingElementIndex === -1"
            class="checklist-actions"
          >
            <IconButton
              class="drag-icon"
              
            >
              <IconMenuOrder class="text-xl" />
            </IconButton>
            <ConfirmButton
              :popupTitle="$t('task.Are_you_sure_you_want_to_delete_this_checklist_item')"
              :popupSubTitle="$t('settings.cant_undone')"
              @confirm="deleteElement(i)"
            >
              <template #menu-trigger>
                <IconButton>
                  <IconTrash class="text-xl" />
                </IconButton>
              </template>
            </ConfirmButton>
          </div>
        </template>
      </Checkbox>
    </div>
    <div class="element add-checkbox-item" v-if="addingMode">
      <CloseButton @click="cancelAdding" />
      <EditorInput
        ref='input'
        class="input new-item"
        :title="$t('task.Type_item')"
        :alwaysOpen="false"
        :mentionButton="false"
        :attachmentButton="false"
        :recordingButton="false"
        emojiPickerBelow
        :isHideFormat="true"
        @submit='addEl'
        @blur='cancelAdding'
      />
    </div>
    <div class="element add-checkbox-item" v-else>
      <SimpleButton
        :text="$t('task.ADD_A_CHECKLIST_ITEM')"
        @click='activateAddingMode'/>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import MarkdownIt from 'markdown-it';
import Sortable from 'sortablejs';
import Checkbox from '@/components/elements/Checkbox';
import EditorInput from '@/components/elements/EditorInput';
import IconButton from '@/components/elements/IconButton';
import SimpleButton from '@/components/elements/SimpleButton';
import CloseButton from '@/components/elements/CloseButton';
import ConfirmButton from '@/components/elements/ConfirmButton';
import { IconMenuOrder, IconTrash } from '@iconify-prerendered/vue-tabler';

const props = defineProps({
  checklist: String,
  autoFocus: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update', 'cancel']);

const store = useStore();
const { t } = useI18n();

const checklistRef = ref(null);
const input = ref(null);
const addingMode = ref(false);
const editingElementIndex = ref(-1);

let sortable = null;

const mobileMode = computed(() => store.state.system.mobileMode);

const md = new MarkdownIt({
  breaks: true,
  linkify: true,
});


async function activateAddingMode() {
  addingMode.value = true;
  await nextTick();
  input.value?.activateIfAllowed();
}
const defaultRender = md.renderer.rules.link_open
  || ((tokens, idx, options, env, self) => self.renderToken(tokens, idx, options));

md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
  const newTokens = [...tokens];
  const aIndex = newTokens[idx].attrIndex('target');
  if (aIndex > -1) {
    newTokens[idx].attrs[aIndex][1] = '_blank';
  }
  const hIndex = newTokens[idx].attrIndex('href');
  if (hIndex >= 0 && newTokens[idx].attrs[hIndex][1].match(/#[A-z0-9]{22}/)) {
    const cIndex = newTokens[idx].attrIndex('class');
    if (cIndex < 0) {
      newTokens[idx].attrPush(['class', 'user-tag']);
    }
  }
  const dIndex = newTokens[idx].attrIndex('draggable');
  if (dIndex < 0) {
    newTokens[idx].attrPush(['draggable', false]);
  } else {
    newTokens[idx].attrs[dIndex][1] = false;
  }
  return defaultRender(newTokens, idx, options, env, self);
};

md.renderer.rules.image = (tokens, idx) => {
  const link = tokens[idx].attrs[0][1];
  return link;
};

const list = computed(() => {
  return props.checklist
    ? JSON.parse(props.checklist).map(el => ({ ...el, keyId: Math.random() }))
    : [];
});

const render = computed(() => {
  return txt => md.render(txt);
});

function getEditorValue(el) {
  if (!el) return '';
  if (!el.nameDelta) return el.name || '';
  if (typeof el.nameDelta === 'string') {
    try {
      return JSON.parse(el.nameDelta);
    } catch (e) {
      return el.name || '';
    }
  }
  return el.nameDelta;
}

function getRenderValue(el) {
  if (!el) return '';
  if (el.nameHTML) return el.nameHTML;
  return render.value(`${el.name || ''}`);
}

function initSortable() {
  if (sortable) {
    sortable.destroy();
  }
  
  if (!checklistRef.value) return;

  sortable = new Sortable(checklistRef.value, {
    scroll: true,
    scrollSensitivity: 200,
    scrollSpeed: mobileMode.value ? 5 : 10,
    group: "checklist",
    sort: true,
    delay: 300,
    delayOnTouchOnly: true,
    touchStartThreshold: 0,
    disabled: false,
    animation: 150,
    easing: "cubic-bezier(1, 0, 0, 1)",
    handle: mobileMode.value ? ".drag-icon" : ".element",
    draggable: ".element",
    filter: ".label .input-wrapper, .button-wrapper, .add-checkbox-item",
    preventOnFilter: true,
    dataIdAttr: 'data-checklist-id',
    ghostClass: "sortable-ghost",
    chosenClass: "sortable-chosen",
    dragClass: "sortable-drag",
    swapThreshold: 1,
    invertSwap: false,
    invertedSwapThreshold: 1,
    forceFallback: true,
    fallbackTolerance: 3,
    dragoverBubble: false,
    removeCloneOnHide: true,
    emptyInsertThreshold: 5,

    onEnd: function (evt) {
      const oldIndex = evt.oldIndex;
      const newIndex = evt.newIndex;
      
      if (oldIndex !== newIndex) {
        const newList = [...list.value];
        const [movedItem] = newList.splice(oldIndex, 1);
        newList.splice(newIndex, 0, movedItem);
        emit('update', newList);
      }
      
      // Workaround for mobile: remove class that might not be removed automatically
      document.body.classList.remove('smooth-dnd-disable-touch-action');
    }
  });
}

function destroySortable() {
  if (sortable) {
    sortable.destroy();
    sortable = null;
  }
}

function update(e, i) {
  const newList = [...list.value];
  if (!e.text) {
    newList.splice(i, 1);
  } else {
    newList[i].name = e.text;
    newList[i].nameHTML = e.html || '';
    newList[i].nameDelta = e.delta || null;
  }
  emit('update', newList);
  editingElementIndex.value = -1;
}

function check(i, status) {
  const newList = [...list.value];
  newList[i].state = status;
  emit('update', newList);
}

async function addEl(e) {
  if (!e) {
    return;
  }
  const newList = [...list.value];
  newList.push({
    state: false,
    name: e.text,
    nameHTML: e.html || '',
    nameDelta: e.delta || null,
  });
  emit('update', newList);
  await nextTick();
  input.value?.activateIfAllowed();
}

function deleteElement(index) {
  const newList = [...list.value];
  newList.splice(index, 1);
  emit('update', newList);
}

function setEditingIndex(e, i) {
  if (e) {
    editingElementIndex.value = i;
  } else {
    editingElementIndex.value = -1;
  }
}

async function resumeEdition() {
  nextTick(() => {
    document.querySelector('.new-item > .title')?.click();
  });
}

function cancelAdding() {
  addingMode.value = false;
  if (list.value && !list.value.length) {
    emit('cancel');
  }
}

onMounted(() => {
  console.log('✔️ checklist mounted');

  if (props.autoFocus) {
    nextTick(() => {
      input.value?.activateIfAllowed();
    });
  }
  if (!props.checklist || props.checklist === '[]') {
    addingMode.value = true;
  }
  
  nextTick(() => {
    initSortable();
  });
});

onUnmounted(() => {
  destroySortable();
});

watch(() => list.value.length, () => {
  nextTick(() => {
    initSortable();
  });
});
</script>

<style lang='scss' scoped>
  .drag-icon {
    cursor: grab;
  }

  :deep(.title) {
    min-height: 16px;
  }
  .checklist-wrapper {
    display: flex;
    flex-direction: column;
    justify-content: center;
    .element {
      display: flex;
      align-items: center;
      min-height: 42px;
      padding-top: 8px;
      padding-left: 10px;
      margin-left: -10px;
      .input {
        width: 100%;
        &.new-item {
          :deep(.title) {
            font-style: italic;
          }
        }
      }
      .checkbox {
        margin-top: 5px;
        height: 16px;
        width: 16px;
        cursor: pointer;
      }
    }
    .edit-btn {
      margin-right: 5px;
      &.active-button:hover:not(:disabled) {
        color: var(--red);
      }
    }

    .checklist-actions {
      display: flex;
      gap: 10px;
      align-items: center;

      .edit-btn {
        cursor: pointer;
      }
    }

    /* matches the single-line editor height so toggling button ↔ input doesn't shift */
    .element.add-checkbox-item {
      min-height: 3.375rem;
      gap: 10px;
      .label {
        width: 100%;
      }
    }
  }
</style>
